<template>
  <label class="block my-3">
    {{ t(configs.label) }}
  </label>
  <div v-if="configs.options.displayVariables">
    <VariablesButton
      :variables-list="enrichedVariables"
      @on-choose-variable="insertContent"
    />
  </div>
  <div class="p-fluid">
    <div class="codemirror-wrapper">
      <div class="codemirror-header">
        <i
          class="fas fa-expand-alt expand-code-btn"
          :title="t('templateBuilder.codeEditor.expandToModal')"
          @click="openCodeModal"
        />
      </div>
      <VCodeMirror
        ref="codeMirrorEditorRef"
        v-model:value="inputValue"
        :mode="{ name: 'text/html' }"
        @blur="onBlur"
        @paste="handleValueChange"
      />
    </div>
  </div>
  <div class="p-fluid">
    <TabView>
      <TabPanel
        :header="t('templateBuilder.panels.cssPanel.header')"
      >
        <CssPanel
          :widget-id="widgetId"
        />
      </TabPanel>
      <TabPanel
        :header="t('templateBuilder.panels.cssPanelMediaQueries.header')"
      >
        <CssPanelMediaQueries
          :widget-id="widgetId"
        />
      </TabPanel>
    </TabView>
  </div>

  <!-- Modal CodeMirror -->
  <CodeMirrorModal
    v-model:visible="showCodeModal"
    v-model:code="inputValue"
    :language="'html'"
    :title="t('templateBuilder.fields.customHtml.modalTitle')"
    :show-variables="configs.options.displayVariables"
    :variables-list="configs.options.variablesList || []"
    @apply="handleModalApply"
  />
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  PropType,
  Ref,
  ref,
  watch,
  computed,
  onBeforeUnmount,
} from 'vue';

import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

import { VCodeMirror } from 'vue3-code-mirror';
import { useI18n } from 'vue-i18n';
import { FieldConfig, ParserFieldObject, Property } from '@/types';

import 'codemirror/mode/htmlmixed/htmlmixed';
import beautifier from 'js-beautify';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';

import CssPanel from '@/components/template-builder/panels/CssPanel.vue';
import CssPanelMediaQueries from '@/components/template-builder/panels/CssPanelMediaQueries.vue';
import CodeMirrorModal from '@/components/common/CodeMirrorModal.vue';

import { useStore } from '@/store';
import { getVariablesWithCollectionContext } from '@/composables/template-builder/collectionContext';

export default defineComponent({
  name: 'CustomHtml',
  components: {
    VariablesButton,
    VCodeMirror,
    TabView,
    TabPanel,
    CssPanel,
    CssPanelMediaQueries,
    CodeMirrorModal,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const store = useStore();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties ?? []);
    const inputValue: Ref<string> = ref(properties.value[0].value);
    const cursorPosition = ref({ line: 0, ch: 0 });
    const codeMirrorEditorRef = ref();
    const enrichedVariables = ref(props.configs.options.variablesList || []);
    const showCodeModal = ref(false);

    const currentActiveItemData = computed(() => store.getters['liveEditor/getActiveItemData']);
    const widgetId = computed(() => {
      if (currentActiveItemData.value && currentActiveItemData.value.selector) {
        return currentActiveItemData.value.selector.replace('#', '');
      }

      // Fallback: extraire l'ID du widget depuis parserValues.selector
      if (props.parserValues.selector) {
        const match = props.parserValues.selector.match(/#(spm_widget_[^.\s]+)/);
        if (match) {
          return match[1];
        }
      }

      return '';
    });

    const handleValueChange = () => {
      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = inputValue.value;
        return updatedProperty;
      });

      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    const insertContent = (value: string|number|undefined) => {
      codeMirrorEditorRef.value.editor.getDoc().replaceRange(value, cursorPosition.value);
      handleValueChange();
    };

    const onBlur = (editor: Record<string, Function>) => {
      cursorPosition.value = {
        line: editor.getCursor().line,
        ch: editor.getCursor().ch,
      };

      const currentValue = inputValue.value;
      let newValue;

      // eslint-disable-next-line no-restricted-syntax
      for (const prop of properties.value) {
        newValue = prop.value;
      }

      if (currentValue !== newValue) {
        store.commit('liveEditor/setWaitForChanges', true);
        handleValueChange();
      }
    };

    onMounted(async () => {
      codeMirrorEditorRef.value.editor.setOption('theme', 'default');

      // Enrich variables with collection context
      if (props.configs.options.variablesList && props.parserValues.selector) {
        enrichedVariables.value = await getVariablesWithCollectionContext(
          props.parserValues.selector,
          props.configs.options.variablesList,
        ) as never[];
      }
    });

    onBeforeUnmount(() => {
      codeMirrorEditorRef.value.cleanEvent();
    });

    watch(() => properties.value[0].value, (values, oldValues) => {
      if (values !== oldValues) {
        inputValue.value = beautifier.html(properties.value[0].value);
      }
    }, { immediate: true });

    const openCodeModal = () => {
      showCodeModal.value = true;
    };

    const handleModalApply = (code: string) => {
      inputValue.value = code;
      handleValueChange();
    };

    return {
      t,
      inputValue,
      enrichedVariables,
      codeMirrorEditorRef,
      widgetId,
      insertContent,
      onBlur,
      handleValueChange,
      showCodeModal,
      openCodeModal,
      handleModalApply,
    };
  },
});
</script>

<style scoped lang="scss">
:deep() .CodeMirror {
  height: 500px !important;
}

/* Styles pour le wrapper CodeMirror avec bouton d'élargissement */
.codemirror-wrapper {
  position: relative;
  width: 100%;
}

.codemirror-header {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;

  .expand-code-btn {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 6px 8px;
    cursor: pointer;
    color: #607D8B;
    font-size: 12px;
    transition: all 0.2s ease;

    &:hover {
      background: #f8f9fa;
      color: #495057;
      transform: scale(1.05);
    }
  }
}
</style>
